import { type HostFloorPlanTable } from 'restaurantAdmin/floorPlans/apiHelpers';
import { Button, ButtonVariants } from '@components/button/Button';
import {
  FloorPlan,
  type FloorPlanTablesRenderer,
} from '@components/floorPlan/FloorPlan';
import FloorPlanTable from '@components/floorPlan/FloorPlanTable';
import cx from 'classnames';
import { useWatch } from 'react-hook-form';
import typography from '~styles/typography.scss';
import { SERVICE_STATUS_COLORS } from '../serviceStatus/ServiceStatus';
import { ServiceStatusIcon } from '../serviceStatus/ServiceStatusIcon';
import { ServerBadge } from '../sidePanel/servers/ServerBadge';
import { useServerFormContext } from '../sidePanel/servers/ServerFormContext';
import { useReservationServiceContext } from '../state/ReservationServiceContext';
import styles from './AssignServerModeFloorPlan.scss';

interface AssignServerModeFloorPlanProps {
  backgroundSrc?: string;
  className?: string;
  tables: HostFloorPlanTable[];
}

const HARDCODED_SCALE_OVERRIDE = 0.33;

export const AssignServerModeFloorPlan = ({
  backgroundSrc,
  className,
  tables,
}: AssignServerModeFloorPlanProps) => {
  const { shouldShowServiceStatuses } = useReservationServiceContext();
  const {
    form: { control, setValue },
  } = useServerFormContext();

  const formFloorPlanTableIds = useWatch({
    control,
    name: 'floorPlanTableIds',
  });

  const formServerName = useWatch({
    control,
    name: 'name',
  });

  const formServerColor = useWatch({
    control,
    name: 'hexColor',
  });

  const formServerId = useWatch({
    control,
    name: 'id',
  });

  const clearSelectedFloorPlanTables = (): void => {
    setValue('floorPlanTableIds', []);
  };

  const floorPlanTablesRenderer: FloorPlanTablesRenderer = (tableIconScale) =>
    tables.map((floorPlanTable) => {
      const {
        id,
        iconName,
        iconWidthScale,
        left,
        name,
        orientation,
        top,
        assignedServer,
        seatedOccupant,
      } = floorPlanTable;

      const isSeated = seatedOccupant !== null;
      const isSelected = formFloorPlanTableIds.includes(id);

      const toggleTableSelection = (): void => {
        if (isSelected) {
          setValue(
            'floorPlanTableIds',
            formFloorPlanTableIds.filter((tableId: string) => tableId !== id),
          );
        } else {
          setValue('floorPlanTableIds', [...formFloorPlanTableIds, id]);
        }
      };

      const shouldShowServerBadge =
        (assignedServer && assignedServer.id !== formServerId) || isSelected;
      const shouldShowCompoundRow =
        (isSeated && shouldShowServiceStatuses) || shouldShowServerBadge;

      return (
        <FloorPlanTable
          key={id}
          left={left}
          top={top}
          tableIconScale={tableIconScale}
        >
          <button
            aria-checked={isSelected}
            aria-label={isSelected ? `Deselect ${name}` : `Select ${name}`}
            className={styles.button}
            data-tooltip-id={name}
            name={name}
            onClick={toggleTableSelection}
            role="checkbox"
            type="button"
          >
            <FloorPlanTable.Icon
              iconName={iconName}
              iconWidthScale={iconWidthScale}
              orientation={orientation}
              tableIconScale={tableIconScale}
            />

            <div className={styles.overlay}>
              {shouldShowCompoundRow && (
                <div className={styles.row}>
                  {isSeated && shouldShowServiceStatuses && (
                    <span
                      className={styles.serverStatusBackground}
                      style={{
                        backgroundColor:
                          SERVICE_STATUS_COLORS[seatedOccupant.serviceStatus],
                      }}
                    >
                      <ServiceStatusIcon
                        serviceStatus={seatedOccupant.serviceStatus}
                        className={styles.serviceStatusIcon}
                        scale={HARDCODED_SCALE_OVERRIDE}
                      />
                    </span>
                  )}

                  {assignedServer && assignedServer.id !== formServerId && (
                    <ServerBadge
                      variant="floorPlanYAxisStack"
                      serverName={assignedServer.name}
                      badgeColor={assignedServer.hexColor}
                    />
                  )}
                  {isSelected && (
                    <ServerBadge
                      variant="floorPlanYAxisStack"
                      serverName={formServerName || '?'}
                      badgeColor={formServerColor}
                    />
                  )}
                </div>
              )}

              <div
                className={cx(styles.row, typography.t3_12, styles.tableName)}
              >
                {name}
              </div>
            </div>
          </button>
        </FloorPlanTable>
      );
    });

  return (
    <div data-testid="assign-server-floor-plan">
      <FloorPlan
        backgroundSrc={backgroundSrc}
        className={cx(styles.floorPlanContainer, className)}
        floorPlanTablesRenderer={floorPlanTablesRenderer}
      />
      <div className={styles.actions}>
        <p className={typography.t1}>Click tables to assign this server to.</p>
        <Button
          variant={ButtonVariants.Tertiary}
          label="Clear Assignment(s)"
          onClick={clearSelectedFloorPlanTables}
        />
      </div>
    </div>
  );
};
